import React from 'react';
import {Divider, Stack, Typography} from '@mui/material';
import {DataId, StyleSx, Sx} from 'app/types/common';
import {packSx} from 'app/util/packSx/packSx';
import {
  BaseBillingApi,
  HistoryItem,
  InvoiceAmount,
  InvoiceDate,
  InvoiceNumber,
} from 'app/components/entities/billing/base';
import {FilesBilling} from 'app/components/entities/billing/files';

interface Props extends Sx {
  invoices: FilesBilling.ArchiveInvoice[];
  teamId: string;
}

export function InvoicesArchive({sx, invoices, teamId}: Props) {
  return (
    <Stack divider={<Divider orientation="horizontal" />} sx={sx}>
      <Header />

      {invoices.map((invoice) => {
        return (
          <ArchiveItem
            dataId={`file-invoice-${invoice.invoice_id}`}
            key={invoice.invoice_id}
            invoice={invoice}
            teamId={teamId}
          />
        );
      })}
    </Stack>
  );
}

const gridSx: StyleSx = {
  display: 'grid',
  gap: 1,
  gridTemplateColumns: '100px 120px repeat(7, 1fr)',
};

function Header() {
  return (
    <HistoryItem sx={packSx(gridSx, {fontWeight: 600})}>
      <Typography variant="inherit">Date</Typography>

      <Typography variant="inherit">Number</Typography>

      <Typography variant="inherit" justifySelf="end">
        Stored
      </Typography>

      <Typography variant="inherit" justifySelf="end">
        Storage fee
      </Typography>

      <Typography variant="inherit" justifySelf="end">
        Transferred
      </Typography>

      <Typography variant="inherit" justifySelf="end">
        Transfer fee
      </Typography>

      <Typography variant="inherit" justifySelf="end">
        Total
      </Typography>

      <Typography variant="inherit" justifySelf="end">
        Paid
      </Typography>

      <Typography variant="inherit" justifySelf="end">
        Due
      </Typography>
    </HistoryItem>
  );
}

interface ItemProps extends DataId {
  invoice: FilesBilling.ArchiveInvoice;
  teamId: string;
}

function ArchiveItem({dataId, teamId, invoice}: ItemProps) {
  return (
    <HistoryItem dataId={dataId} sx={gridSx}>
      <InvoiceDate dataId="invoice-date" timestamp={invoice.issued_at} />

      <InvoiceNumber
        dataId="invoice-number"
        number={invoice.invoice_number}
        href={
          invoice.available_for_download
            ? BaseBillingApi.getInvoiceUrl(teamId, invoice.invoice_id)
            : undefined
        }
      />

      <Typography data-id="invoice-storage-size" justifySelf="end">
        {invoice.storage_gb.toLocaleString(undefined, {maximumFractionDigits: 2})} GB
      </Typography>

      <InvoiceAmount dataId="invoice-storage-fee" amount={invoice.storage_fee_usd} />

      <Typography data-id="invoice-transfer-size" justifySelf="end">
        {invoice.transfer_gb.toLocaleString(undefined, {maximumFractionDigits: 2})} GB
      </Typography>

      <InvoiceAmount dataId="invoice-transfer-fee" amount={invoice.storage_fee_usd} />

      <InvoiceAmount dataId="invoice-total" amount={invoice.total_amount_usd} />

      <InvoiceAmount dataId="invoice-paid" amount={invoice.amount_paid_usd} />

      <InvoiceAmount dataId="invoice-due" amount={invoice.amount_remaining_usd} />
    </HistoryItem>
  );
}
